import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  ColumnFilterOption,
  ColumnSizing,
  TableContentTransformations,
} from 'components/ReorderResizeTable/types';
import {
  DATA_ROOM_MEMBER_STATUS,
  DataRoomBuySideGroup,
  DataRoomMember,
  PreviousGroupChats,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import {
  getArrayLength,
  getHasElementsInArray,
  getIsArrayEmpty,
  getMemberInitials,
  getMultiSortComparator,
  getShowGroupChat,
  getUserFullName,
  normalizeEmail,
} from 'utils';
import { NewTag } from 'router/subrouters/DataRoom/components/NewTag';
import LeadTag from 'router/subrouters/DataRoom/components/LeadTag';
import RoleTag from 'router/subrouters/DataRoom/subrouters/components/RoleTag';
import {
  qaRoleTagLabelMap,
  roleTagLabelMap,
} from 'router/subrouters/DataRoom/constants';
import MemberRoleInstantChange from 'router/subrouters/DataRoom/components/DataRoomMembersTable/MemberRoleInstantChange';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  MEMBER_DRAWER_OPTIONS,
  MemberDrawerOption,
} from 'router/subrouters/DataRoom/components/DocumentAvailabilityDrawer/data';
import { isDataRoomAdminOrOwner } from 'router/subrouters/DataRoom/utils';
import ResolvePopup from 'components/ResolvePopup';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { DropdownOption } from 'models/Dropdown';
import { COLOR_VARIANT } from 'models/Colors';
import { ALIGNMENT } from 'models/Global';
import { IconProp } from 'models/Icon';
import ChatIcon from 'icons/Chat.icon';
import EyeIcon from 'icons/Eye.icon';
import AskQuestionIcon from 'icons/AskQuestion.icon';
import DownloadIcon from 'icons/Download.icon';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useCanEditDataRoomMember from '../../hooks/useCanEditDataRoomMember';
import useGroupFeatureDiscovery from '../../hooks/useGroupFeatureDiscovery';
import GroupDiscoveryHint from '../GroupDiscoveryHint';
import RenameGroupBanner from '../RenameGroupBanner';
import useMatchMedia from 'hooks/useMatchMedia';
import useLocalPagination from 'hooks/useLocalPagination';
import { SMOOTH_SCROLL_CONFIGURATION } from 'data/global';
import usePastTime from 'hooks/usePastTime';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import Counter from 'components/Counter';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import useQueryParams from 'hooks/useQueryParams';
import {
  CUSTOM_SIZES,
  DEFAULT_MEMBERS_SORT,
  HEADING_TRANSLATIONS,
  MemberRow,
  MemberTableColumn,
  SORTING_VALUES,
  STATUS_SORT_RANK,
} from './data';
import GroupPill from 'router/subrouters/DataRoom/subrouters/components/GroupPill';
import useDrawerNavigation from 'hooks/useDrawerNavigation';
import { DrawerNavigation } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { getMemberTableColumns } from './utils';

import './MembersFlatTable.styles.scss';

type MembersFlatTableProps = {
  className?: string;
  columnFilterOptions?: Partial<Record<MemberTableColumn, ColumnFilterOption>>;
  members: DataRoomMember[];
  isBuySide?: boolean;
  showChat?: boolean;
  showGroup?: boolean;
  // Engagement stats (status, views, questions, downloads, last seen) are only
  // collected for the sell side's view of the room, so a buy-side member
  // looking at their own team gets the plain columns.
  showActivity?: boolean;
  // When set, the sort lives in this query param instead of local state.
  sortParam?: string;
  // When set, the table is paginated on the FE: the page lives in this query
  // param and only `limit` members render at a time. Left out, every member is
  // rendered in one list.
  pageParam?: string;
  limit?: number;
  columns?: MemberTableColumn[];
  columnSizing?: ColumnSizing;
  onReorder?: (columns: MemberTableColumn[]) => void;
  onColumnSizingChange?: (columnSizing: ColumnSizing) => void;
  // Whether the user can change any of that from here. The waiting room shows
  // the same columns as the main table but does not let you rearrange them.
  canManageColumns?: boolean;
  memberGroupMap?: Record<string, DataRoomBuySideGroup>;
  previousGroupChatsMap?: Record<string, PreviousGroupChats>;
  emptyMessage?: string;
  // The group that just grew past its single member: its first row on screen
  // carries the "rename this group" suggestion.
  renameSuggestionGroupId?: string;
  selectedMemberId?: string;
  onDismissRenameSuggestion?: () => void;
  onRowClick?: (member: DataRoomMember) => void;
  onEdit: (member: DataRoomMember, tab?: MemberDrawerOption) => void;
  onDeleteMember?: (member: DataRoomMember) => void;
  onAcceptRequest?: (member: DataRoomMember) => void;
  onRejectRequest?: (member: DataRoomMember) => void;
  onEditMemberRole?: (
    memberId: string,
    updated: Partial<DataRoomMember>,
  ) => void;
  onGroupClick?: (group: DataRoomBuySideGroup) => void;
  // Replaces the options in the row menu, for tables that manage a member from
  // their own drawer rather than the shared member drawers. Only called for
  // members the user is allowed to edit.
  rowActionOptions?: (member: DataRoomMember) => DropdownOption[];
  renderDrawers?: (navigation: DrawerNavigation) => ReactNode;
};

const stopClick = (e: React.MouseEvent) => e.stopPropagation();

// Keeps each member's row (and its loaded avatar) with the member when the list
// is re-sorted, instead of reusing rows by position.
const getMemberRowId = ({ _id }: MemberRow) => _id;

const MembersFlatTable: React.FC<MembersFlatTableProps> = (props) => {
  const {
    className,
    members = [],
    isBuySide = false,
    showChat = false,
    showGroup = false,
    showActivity = false,
    sortParam,
    pageParam,
    limit,
    columns,
    columnSizing,
    canManageColumns = false,
    memberGroupMap = {},
    previousGroupChatsMap = {},
    emptyMessage,
    renameSuggestionGroupId,
    columnFilterOptions,
    selectedMemberId,
    onReorder,
    onColumnSizingChange,
    onDismissRenameSuggestion,
    onRowClick,
    onEdit,
    onDeleteMember,
    onAcceptRequest,
    onRejectRequest,
    onEditMemberRole,
    onGroupClick,
    rowActionOptions,
    renderDrawers,
  } = props;

  const { params, setQueryParam } = useQueryParams<Record<string, string>>();

  const [localSort, setLocalSort] = useState(DEFAULT_MEMBERS_SORT);

  const sort = sortParam
    ? params[sortParam] || DEFAULT_MEMBERS_SORT
    : localSort;

  const updateSort = useCallback(
    (value: string) =>
      sortParam ? setQueryParam(sortParam, value) : setLocalSort(value),
    [sortParam, setQueryParam],
  );

  const isTouch = useMatchMedia('isTouchDevice');

  const tableRef = useRef<HTMLDivElement>(null);

  // Paging from the bottom of a full table would otherwise leave the user
  // looking at the footer of the next page. The data room scrolls an inner
  // container rather than the window, so the table scrolls itself into view
  // instead of moving the window to a computed offset.
  const scrollToStart = useCallback(
    () =>
      setTimeout(
        () =>
          tableRef.current?.scrollIntoView({
            ...SMOOTH_SCROLL_CONFIGURATION,
            block: 'start',
          }),
        0,
      ),
    [],
  );

  const getPastTime = usePastTime();

  const { openChatDrawer } = useContext(DataRoomContext);
  const { currentUser } = useContext(CurrentUserContext);

  const { unreadSessionIds } = currentUser ?? {};

  const getCanEditMember = useCanEditDataRoomMember();

  const {
    shouldPulseGroupRow,
    shouldPulseGroupName,
    discoverGroupRow,
    discoverGroupName,
  } = useGroupFeatureDiscovery();

  const { t } = useTranslation();

  const classes = classNames('members-flat-table', className);

  const availableColumns = useMemo(
    () => getMemberTableColumns({ showGroup, showActivity, showChat }),
    [showActivity, showChat, showGroup],
  );

  // Sort value mirrors what the cell displays — text columns lower-cased for
  // case-insensitive comparison, engagement columns as numbers. Name has no
  // email fallback: invited members sort as "Invited".
  const sortValueGetters = useMemo<
    Record<string, (member: DataRoomMember) => string | number>
  >(
    () => ({
      name: ({ isAccepted, firstName, lastName }) =>
        (isAccepted
          ? getUserFullName(firstName, lastName)
          : t('invited')
        ).toLowerCase(),
      group: ({ _id }) => (memberGroupMap[_id]?.name ?? '').toLowerCase(),
      // The role cells show a translated label rather than the raw enum, so
      // that is what has to be compared: "Question Approver" reads as
      // "Approve" and belongs under A, not Q.
      files: ({ role }) =>
        role ? t(roleTagLabelMap[role] ?? role).toLowerCase() : '',
      qa: ({ qaRole }) =>
        qaRole ? t(qaRoleTagLabelMap[qaRole] ?? qaRole).toLowerCase() : '',
      status: ({ status }) => STATUS_SORT_RANK[status] ?? 0,
      views: ({ viewCount }) => viewCount ?? 0,
      questions: ({ questionCount }) => questionCount ?? 0,
      downloads: ({ downloadCount }) => downloadCount ?? 0,
      lastSeen: ({ lastSeen }) => (lastSeen ? new Date(lastSeen).getTime() : 0),
    }),
    [memberGroupMap, t],
  );

  const sortedMembers = useMemo(
    () =>
      [...members].sort(
        getMultiSortComparator<DataRoomMember>(sort, sortValueGetters),
      ),
    [members, sort, sortValueGetters],
  );

  // Paged after sorting, so the sort still orders every member rather than
  // rearranging the rows that happen to be on screen.
  const {
    pageItems,
    totalPages,
    limit: pageLimit,
  } = useLocalPagination(sortedMembers, {
    limit,
    pageParam,
    isEnabled: !!pageParam,
  });

  // Discovery hints appear once each: the "member settings" hint on the first
  // row of the buy-side table, the "manage your group" hint on the first pill.
  const firstMemberId = sortedMembers[0]?._id;

  const firstGroupMemberId = useMemo(
    () => sortedMembers.find(({ _id }) => memberGroupMap[_id])?._id,
    [memberGroupMap, sortedMembers],
  );

  // The rename suggestion belongs to a group rather than to a member, so it
  // hangs off that group's first row on screen — the row the broker added from,
  // rather than wherever the new members happen to sort.
  const renameSuggestionMemberId = useMemo(
    () =>
      renameSuggestionGroupId
        ? pageItems.find(
            ({ _id }) => memberGroupMap[_id]?._id === renameSuggestionGroupId,
          )?._id
        : undefined,
    [memberGroupMap, pageItems, renameSuggestionGroupId],
  );

  const renderName = useCallback(
    (member: DataRoomMember) => {
      const {
        isAccepted,
        requestedDataRoomAccess,
        firstName,
        lastName,
        isLead,
        isLeadWithdrawn,
        requestedDataRoomAccessAt,
        email,
        user,
      } = member;

      const fullName = getUserFullName(firstName, lastName);

      return (
        <div className="name-cell">
          {/* Invited members have no user record yet, so fall back to initials
              derived from the invite's name (empty falls back to the logo). */}
          <AvatarWithInitials
            className="name-cell__avatar"
            {...(user
              ? { user }
              : { initials: getMemberInitials(fullName, email) })}
          />

          <div className="name-cell__info">
            <span
              className={classNames('name', {
                invited: !isAccepted || requestedDataRoomAccess,
              })}
            >
              {isAccepted ? fullName : t('invited')}

              {isLead && <LeadTag isWithdraw={isLeadWithdrawn} />}

              {requestedDataRoomAccess && (
                <NewTag
                  variant="green"
                  daysAgo={3}
                  time={requestedDataRoomAccessAt}
                />
              )}

              {/* Both discovery hints belong to the group feature, so they
                  only show on the table that has the group column. */}
              {showGroup &&
                member._id === firstMemberId &&
                shouldPulseGroupRow && (
                  <GroupDiscoveryHint
                    title="groupDiscoveryMemberRowTitle"
                    description="groupDiscoveryMemberRowDescription"
                    // Card opens on hover/tap of the dot; closing it marks
                    // discovered. The member drawer opens on a normal row click
                    // (handled below).
                    onClose={discoverGroupRow}
                    anchor={
                      <button
                        type="button"
                        aria-label={t('groupDiscoveryMemberRowTitle')}
                        className={classNames(
                          'group-discovery-hint__dot',
                          'group-discovery-hint__dot--pulsing',
                        )}
                      />
                    }
                  />
                )}
            </span>

            {email && <span className="email">{normalizeEmail(email)}</span>}
          </div>
        </div>
      );
    },
    [discoverGroupRow, firstMemberId, shouldPulseGroupRow, showGroup, t],
  );

  const renderGroup = useCallback(
    (member: DataRoomMember) => {
      const group = memberGroupMap[member._id];

      if (!group) return null;

      // The group pill shows the dot when the shared group chat has unread
      // messages (the 1-on-1 dot lives on the chat button instead).
      const hasUnread = Boolean(
        group.activeChatSessionId &&
          unreadSessionIds?.includes(group.activeChatSessionId),
      );

      // Before discovery, the first group pill pulses and shows the discovery
      // card on hover (desktop) / tap (touch). A normal click opens the group
      // drawer — except a touch tap while pulsing, which opens the card.
      const isFirstGroup = member._id === firstGroupMemberId;

      // The group this member just became the second of gets the rename
      // suggestion on their cell. It takes the pill as its anchor, so the
      // discovery hint stands down rather than opening a second banner on it.
      const hasRenameSuggestion =
        !!onDismissRenameSuggestion && member._id === renameSuggestionMemberId;

      const isDiscoveryPill =
        isFirstGroup && shouldPulseGroupName && !hasRenameSuggestion;

      const pill = (
        <GroupPill
          name={group.name}
          count={getArrayLength(group.dataRoomMembers)}
          isPulsing={isDiscoveryPill}
          {...(!(isTouch && isDiscoveryPill) && {
            onClick: () => {
              onGroupClick?.(group);
              discoverGroupName();
            },
          })}
        />
      );

      return (
        <div className="group-cell" onClick={stopClick}>
          {hasRenameSuggestion ? (
            <RenameGroupBanner
              group={group}
              anchor={pill}
              onClose={onDismissRenameSuggestion}
            />
          ) : isDiscoveryPill ? (
            <GroupDiscoveryHint
              title="groupDiscoveryGroupNameTitle"
              description="groupDiscoveryGroupNameDescription"
              onClose={discoverGroupName}
              anchor={pill}
            />
          ) : (
            pill
          )}

          {hasUnread && <Counter onlyDot className="unread-dot" />}
        </div>
      );
    },
    [
      discoverGroupName,
      firstGroupMemberId,
      isTouch,
      memberGroupMap,
      onDismissRenameSuggestion,
      onGroupClick,
      renameSuggestionMemberId,
      shouldPulseGroupName,
      unreadSessionIds,
    ],
  );

  const renderRole = useCallback(
    (member: DataRoomMember, isQaRole = false) => {
      const { _id, role, qaRole, userId } = member;

      return (
        <MemberRoleInstantChange
          memberId={_id}
          isBuySide={isBuySide}
          {...(isQaRole ? { qaRole } : { role })}
          {...(getCanEditMember(role, userId) && {
            onChange: onEditMemberRole,
          })}
        />
      );
    },
    [isBuySide, getCanEditMember, onEditMemberRole],
  );

  const renderStatus = useCallback(
    (member: DataRoomMember) => {
      const { status } = member;

      if (!status) return null;

      const isHot = status === DATA_ROOM_MEMBER_STATUS.HOT;

      return (
        <RoleTag
          className="status-tag"
          variant={isHot ? COLOR_VARIANT.RED : COLOR_VARIANT.BLUE}
        >
          {t(isHot ? 'hot' : 'cold')}
        </RoleTag>
      );
    },
    [t],
  );

  const renderMetric = useCallback(
    (count = 0, Icon: IconProp) => (
      <div className="metric-cell">
        <span className="metric-cell__count">{count}</span>
        <Icon />
      </div>
    ),
    [],
  );

  const renderChat = useCallback(
    (member: DataRoomMember) => {
      const { sessionId } = member;

      if (!sessionId) return null;

      const group = memberGroupMap[member._id];
      const previousGroupChats = group
        ? previousGroupChatsMap[group._id]
        : undefined;

      const showGroupChat = getShowGroupChat(
        previousGroupChats?.activeChatSessionId,
        group?.dataRoomMembers ?? [],
      );

      const groupChatSessionId = showGroupChat
        ? previousGroupChats?.activeChatSessionId
        : undefined;

      // Only 1-on-1 chats show the unread dot; the member's own chat session
      // has unread messages.
      const hasUnread = Boolean(unreadSessionIds?.includes(sessionId));

      return (
        <div className="chat-cell">
          <Button
            variant="icon"
            className="chat-button"
            icon={ChatIcon}
            onClick={(e) => {
              e.stopPropagation();
              openChatDrawer(sessionId, groupChatSessionId, previousGroupChats);
            }}
          />

          {hasUnread && <Counter onlyDot className="unread-dot" />}
        </div>
      );
    },
    [memberGroupMap, openChatDrawer, previousGroupChatsMap, unreadSessionIds],
  );

  const renderActions = useCallback(
    (member: DataRoomMember) => {
      if (!getCanEditMember(member.role, member.userId)) return null;

      // Failing an override: a member still waiting on their access request has
      // no activity yet and cannot be deleted; the request itself is what there
      // is to act on. Admins and owners cannot be removed from the data room,
      // matching the drawer, which hides its key actions for them.
      const options: DropdownOption[] = rowActionOptions?.(member) ?? [
        {
          label: t('editPermissions'),
          onClick: () => onEdit(member, MEMBER_DRAWER_OPTIONS.SETTINGS),
        },
        ...(member.requestedDataRoomAccess
          ? [
              {
                label: t('approve'),
                onClick: () => onAcceptRequest(member),
              },
              {
                label: t('decline'),
                alert: true,
                onClick: () => onRejectRequest(member),
              },
            ]
          : [
              {
                label: t('viewActivity'),
                onClick: () => onEdit(member, MEMBER_DRAWER_OPTIONS.ACTIVITY),
              },
              ...(onDeleteMember && !isDataRoomAdminOrOwner(member.role)
                ? [
                    {
                      label: t('deleteMember'),
                      alert: true,
                      onClick: () => onDeleteMember(member),
                    },
                  ]
                : []),
            ]),
      ];

      if (getIsArrayEmpty(options)) return null;

      return (
        // The table scrolls, so the menu has to live outside it to avoid being
        // clipped on the last rows and the right-hand edge.
        <ThreeDotsDropdown
          renderAsPortal
          withBorder={false}
          alignment={ALIGNMENT.VERTICAL}
          portalClassName="member-actions-portal"
          options={options}
        />
      );
    },
    [
      getCanEditMember,
      onAcceptRequest,
      onDeleteMember,
      onEdit,
      onRejectRequest,
      rowActionOptions,
      t,
    ],
  );

  const tableContentTransformations = useMemo<
    TableContentTransformations<MemberRow>
  >(
    () => ({
      name: (member: MemberRow) => renderName(member),
      group: (member: MemberRow) => renderGroup(member),
      files: (member: MemberRow) => (
        <div onClick={stopClick}>{renderRole(member)}</div>
      ),
      qa: (member: MemberRow) => (
        <div onClick={stopClick}>{renderRole(member, true)}</div>
      ),
      status: (member: MemberRow) => renderStatus(member),
      views: (member: MemberRow) => renderMetric(member.viewCount, EyeIcon),
      questionsColumn: (member: MemberRow) =>
        renderMetric(member.questionCount, AskQuestionIcon),
      downloads: (member: MemberRow) =>
        renderMetric(member.downloadCount, DownloadIcon),
      lastSeen: (member: MemberRow) => (
        <span className="last-seen">
          {member.lastSeen ? getPastTime(member.lastSeen) : t('na')}
        </span>
      ),
      teams: ({ teams }: MemberRow) => (
        <div className="teams">
          {getHasElementsInArray(teams) ? (
            teams.map((team) => <RoleTag key={team}>{team}</RoleTag>)
          ) : (
            // A member without a team role gets a placeholder rather than a
            // cell that reads as a rendering glitch.
            <span className="no-team-role">/</span>
          )}
        </div>
      ),
      chatColumn: (member: MemberRow) => renderChat(member),
      actions: (member: MemberRow) => (
        <div className="actions" onClick={stopClick}>
          {renderActions(member)}
        </div>
      ),
    }),
    [
      getPastTime,
      renderActions,
      renderChat,
      renderGroup,
      renderMetric,
      renderName,
      renderRole,
      renderStatus,
      t,
    ],
  );

  const tableHeaderContentTransformations = useMemo<
    TableContentTransformations<MemberRow>
  >(
    () => ({
      status: () => (
        <span className="status-header">
          {t('status')}

          <ResolvePopup onHover popupContent={t('membersStatusPopup')}>
            <InformationcircleIcon />
          </ResolvePopup>
        </span>
      ),
    }),
    [t],
  );

  const onClickRow = useCallback(
    (member: MemberRow) => {
      if (!getCanEditMember(member.role, member.userId)) return;

      onRowClick?.(member);
      // Opening a member drawer the normal way also counts as discovery — but
      // only where the hint was on offer in the first place.
      if (showGroup) discoverGroupRow();
    },
    [discoverGroupRow, getCanEditMember, onRowClick, showGroup],
  );

  const getIsMemberNavigable = useCallback(
    ({ role, userId }: DataRoomMember) => getCanEditMember(role, userId),
    [getCanEditMember],
  );

  const drawerNavigation = useDrawerNavigation({
    rows: pageItems,
    selectedId: selectedMemberId,
    totalPages,
    pageParam,
    getIsNavigable: getIsMemberNavigable,
    onNavigate: onRowClick,
  });

  return (
    // The table scrolls itself, so the ref goes on a wrapper the pager can
    // bring back into view.
    <div ref={tableRef}>
      <ReorderResizeTable<MemberRow>
        className={classes}
        // Dragging and resizing are only offered where the result can be saved.
        isDragDisabled={!canManageColumns}
        isResizeDisabled={!canManageColumns}
        showPlaceholder={false}
        columns={columns ?? availableColumns}
        data={pageItems as MemberRow[]}
        totalPages={totalPages}
        // The "showing x to y of z members" count belongs to the paged tables:
        // an unpaged one already has every member on screen.
        {...(pageParam && {
          totalItems: getArrayLength(sortedMembers),
          limit: pageLimit,
          recordName: 'member',
        })}
        pageParam={pageParam}
        onPaginationClick={scrollToStart}
        getRowId={getMemberRowId}
        customSizes={CUSTOM_SIZES}
        headingTranslations={HEADING_TRANSLATIONS}
        sortingValues={SORTING_VALUES}
        columnFilterOptions={columnFilterOptions}
        sort={sort}
        updateSort={updateSort}
        tableContentTransformations={tableContentTransformations}
        tableHeaderContentTransformations={tableHeaderContentTransformations}
        emptyMessage={emptyMessage}
        onRowClick={onClickRow}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder as (columns: Array<keyof MemberRow>) => void}
      />

      {renderDrawers?.(drawerNavigation)}
    </div>
  );
};

export default MembersFlatTable;
